import { usePathname } from 'next/navigation'
import { useEffect } from 'react'

/**
 * Hook useScrollToHeader
 *
 * Problem:
 * The default scrolling behavior in Next.js 15 **does not work correctly in our project**.
 * After navigation, <Link scroll={true}> should scroll the page to the top, but it does not.
 * The following issues occur:
 * - Next.js does not scroll the page to the top after navigation.
 * - Similar issues have been reported here:
 *    https://github.com/vercel/next.js/discussions/64435
 *    https://stackoverflow.com/questions/58008310/next-js-link-doesnt-render-with-page-scrolled-at-the-top/66316619#66316619
 * - I have not found the exact cause of this behavior yet; further investigation might be needed.
 *
 * Solution:
 * - We use `document.documentElement.scrollTo({ top: 0, behavior: 'instant' })`
 *   instead of `window.scrollTo(0, 0)`, because `window.scrollTo()` resets the previous page's
 *   scroll position, causing the loss of stored scroll when pressing Back.
 * - The `behavior: 'instant'` ensures that scrolling is applied immediately,
 *   preventing flickering or unwanted animations.
 * - This approach guarantees that the page is always scrolled to the top upon navigation.
 *
 * @param forceScroll - When true, always scrolls to top regardless of path comparison.
 *                      Useful for 404 pages where pathname might be the same as lastPath.
 */

export function useScrollToHeader(forceScroll?: boolean) {
  const pathname = usePathname()

  useEffect(() => {
    const lastPath = sessionStorage.getItem('last-path')
    if (lastPath !== pathname || forceScroll) {
      window.scrollTo({ top: 0, behavior: 'instant' })
    }
    // Only update session storage for normal navigation, not forced scrolls
    if (!forceScroll) {
      sessionStorage.setItem('last-path', pathname)
    }
  }, [pathname, forceScroll])
}
